// My jobs in three groups: upcoming · completion to check · done. An external party
// sees its own party's jobs; an internal crew sees every Team Artemis job.
import { useT } from "../i18n";
import { usePageHead } from "../shell/Frame";
import { useRole } from "../hooks/useMe";
import { useMyJobs } from "../hooks/useReads";
import { day, isPast, isSameDay, m2 } from "../lib/format";
import { installationComplete, type InstallerJob } from "../lib/types";
import { useReports } from "../state/reports";
import { JobCard } from "../components/JobCard";
import type { Tone } from "../components/Panel";
import { Button } from "@aiw/ui";

export function MyJobs() {
  const { t, lang } = useT();
  const { isExternal, partyName } = useRole();
  const jobs = useMyJobs();
  const reports = useReports();
  usePageHead(t("tab_jobs"), isExternal ? t("jobs_scope_external", { party: partyName }) : t("jobs_scope_internal"));

  const list = jobs.data ?? [];
  const isDone = installationComplete;
  const completionToCheck = (j: InstallerJob) => !isDone(j) && j.portal_state !== "offered" && (isSameDay(j.install_next_date) || isPast(j.install_next_date));
  const groups: { key: "group_upcoming" | "group_completion_to_check" | "group_done"; rows: InstallerJob[] }[] = [
    { key: "group_completion_to_check", rows: list.filter(completionToCheck) },
    { key: "group_upcoming", rows: list.filter((j) => !isDone(j) && !completionToCheck(j)).sort((a, b) => (a.install_next_date ?? "").localeCompare(b.install_next_date ?? "")) },
    { key: "group_done", rows: list.filter(isDone).sort((a, b) => (b.signed_off_at ?? "").localeCompare(a.signed_off_at ?? "")) },
  ];

  function pillFor(j: InstallerJob): { text: string; tone: Tone } {
    const r = reports[j.ref];
    if (isDone(j)) return { text: j.signed_off_at ? t("pill_done", { date: day(j.signed_off_at, lang) }) : t("pill_install_complete"), tone: "ok" };
    if (completionToCheck(j)) return { text: t("pill_completion_to_check"), tone: "warn" };
    if (j.portal_state === "offered" && r?.offer !== "accepted") return r?.offer === "declined" ? { text: t("offer_declined"), tone: "plain" } : { text: t("pill_offered", { when: day(j.answer_by, lang) }), tone: "warn" };
    if (isSameDay(j.install_next_date)) return { text: t("pill_installing"), tone: "info" };
    if (j.portal_state === "accepted" || r?.offer === "accepted") return { text: t("pill_accepted"), tone: "ok" };
    return { text: t("pill_scheduled"), tone: "ok" };
  }

  if (jobs.isLoading) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  if (jobs.isError) return <div role="alert" className="rounded-lg border p-4"><p className="text-sm text-destructive">{t("work_load_failed")}</p><Button size="touch" className="mt-3" onClick={() => void jobs.refetch()}>{t("retry_work")}</Button></div>;

  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <section key={g.key} className="space-y-2">
          <h2 className="kicker">{t(g.key)} · {g.rows.length}</h2>
          {g.rows.length === 0 ? (
            <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">{t("empty_group")}</p>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {g.rows.map((j) => {
                const p = pillFor(j);
                const bits = [day(j.install_next_date ?? j.signed_off_at ?? null, lang), j.area_m2 != null ? m2(j.area_m2) : null, j.product, j.stairs ? `${j.stairs} ${t("stairs")}` : null].filter(Boolean);
                return <JobCard key={j.ref} job={j} sub={bits.join(" · ")} pill={p.text} tone={p.tone} to={j.portal_state === "offered" && !reports[j.ref]?.offer ? "/" : `/work-order/${j.ref}`} />;
              })}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
